import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useFloatingPanel } from "../hooks/useFloatingPanel";

export interface RowMenuAction {
  label: string;
  /** What pressing it does. Omitted only for a row that opens `children`. */
  onSelect?: () => void;
  /** A nested list this row opens instead of acting. */
  children?: RowMenuAction[];
  /** Ask once before running. Use for anything that cannot be undone. */
  confirm?: string;
  danger?: boolean;
  /** Start a new section: a hairline above this row. */
  divider?: boolean;
}

interface RowMenuProps {
  actions: RowMenuAction[];
  /** Screen-reader name — "дії для «Прочитати книгу»" beats a dozen "дії". */
  label: string;
  /**
   * What the trigger shows. Defaults to the «⋯» every row wears.
   *
   * A word instead: the notes' hover bar spells its menus out — «Відкласти»,
   * «У задачі» — because three dots beside three dots beside three dots is a
   * row of identical doors. Keep `label` starting with that same word, or the
   * accessible name and the visible one stop matching.
   */
  trigger?: ReactNode;
  /** Replaces the trigger's own classes, for a text trigger inside a panel. */
  triggerClass?: string;
  /** Fires on open and on close — for a bar that must stay up while it is open. */
  onOpenChange?: (open: boolean) => void;
}

/** Move focus between the `menuitem`s of one panel. */
function focusItem(panel: HTMLElement | null, from: HTMLElement | null, step: number | "first" | "last") {
  if (!panel) return;
  const items = Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  if (items.length === 0) return;
  // `preventScroll`: the panel is already clamped inside the viewport, and the
  // default scroll-into-view would jerk the page behind it.
  const focus = (el: HTMLElement) => el.focus({ preventScroll: true });
  if (step === "first") return focus(items[0]);
  if (step === "last") return focus(items[items.length - 1]);
  const at = from ? items.indexOf(from) : -1;
  focus(items[(at + step + items.length) % items.length]);
}

/**
 * The "⋯" menu that hangs off a goal, a task or a note row.
 *
 * The panel is a `position: fixed` portal into `<body>`, not an absolutely
 * positioned child. A row menu lives inside scrolling, `overflow`-clipped
 * containers, and the last row of a list used to open a menu that was cut off
 * by its own ancestor — a z-index cannot fix a clip. Geometry (flip up when the
 * bottom is tight, clamp inside the viewport) comes from `useFloatingPanel`.
 *
 * Confirmation is inline rather than a modal: a dialog over a list row costs a
 * layout shift and a second decision, and the only thing behind this menu is a
 * delete. The confirm state resets whenever the menu closes, so a stray tap
 * never leaves a primed "really?" button behind.
 *
 * A row may open a nested list (`children`) instead of acting — «Відкласти…»
 * with its three dates. It flies out to whichever side has room; the chevron
 * follows the side it actually took.
 */
const DOTS_TRIGGER =
  "grid size-8 shrink-0 place-items-center rounded-control text-muted transition-colors hover:bg-surface-2 hover:text-fg";

export default function RowMenu({
  actions,
  label,
  trigger,
  triggerClass,
  onOpenChange,
}: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const announce = useRef(onOpenChange);
  announce.current = onOpenChange;
  useEffect(() => {
    announce.current?.(open);
  }, [open]);
  const [armed, setArmed] = useState<string | null>(null);
  const [submenu, setSubmenu] = useState<string | null>(null);
  const subAnchor = useRef<HTMLButtonElement | null>(null);

  const menu = useFloatingPanel<HTMLButtonElement, HTMLDivElement>(open, {
    align: "end",
    onDismiss: () => setOpen(false),
  });
  const sub = useFloatingPanel<HTMLButtonElement, HTMLDivElement>(submenu !== null, {
    axis: "horizontal",
    align: "start",
    gap: 2,
    // Moving straight from one submenu row to the next never closes the panel,
    // so the label is what tells the hook the anchor is a different button.
    // `armed` too: arming a confirm widens the parent panel, which moves the
    // row this flyout hangs off.
    anchorKey: `${submenu ?? ""}|${armed ?? ""}`,
    onDismiss: () => setSubmenu(null),
  });

  useEffect(() => {
    if (!open) {
      setArmed(null);
      setSubmenu(null);
      return;
    }
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      // Three surfaces now, not one: the trigger stays in the row while both
      // panels live in a portal, so `contains` has to be asked of each.
      if (menu.anchorRef.current?.contains(target)) return;
      if (menu.panelRef.current?.contains(target)) return;
      if (sub.panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      // Escape steps back out of the nested list first, then out of the menu —
      // one press per level, the way a menu is expected to unwind.
      if (event.key !== "Escape") return;
      if (submenu) {
        setSubmenu(null);
        subAnchor.current?.focus({ preventScroll: true });
      } else {
        setOpen(false);
        menu.anchorRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, submenu, menu.anchorRef, menu.panelRef, sub.panelRef]);

  // A portal is not the trigger's DOM neighbour any more, so Tab cannot walk
  // into it. Focus the first row on open, which is also what makes the arrow
  // keys below reachable at all.
  useEffect(() => {
    if (open) focusItem(menu.panelRef.current, null, "first");
  }, [open, menu.panelRef]);

  useEffect(() => {
    if (submenu !== null) focusItem(sub.panelRef.current, null, "first");
  }, [submenu, sub.panelRef]);

  // `whitespace-nowrap` rather than a wider `min-w`: the panel sizes itself to
  // its longest label, so «Повернути в роботу» stops wrapping to two lines
  // without forcing every other menu in the app to be that wide.
  // `elev-hover`, not `surface-2`: the panel itself is `jv-elevated` (bg-elev),
  // and in dark mode `elev` already sits lighter than `surface-2` — hovering
  // to `surface-2` used to read as a dark smudge on the panel, not a highlight.
  const itemClass = (danger?: boolean) =>
    `w-full rounded-control px-2 py-1.5 text-left text-sm whitespace-nowrap transition-colors hover:bg-elev-hover ${
      danger ? "text-danger" : "text-fg"
    }`;

  function run(action: RowMenuAction) {
    const key = action.label;
    if (action.confirm && armed !== key) {
      setArmed(key);
      return;
    }
    setOpen(false);
    menu.anchorRef.current?.focus({ preventScroll: true });
    action.onSelect?.();
  }

  function onMenuKey(event: React.KeyboardEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (event.key === "Tab") {
      // Not prevented: focus moves to the trigger first, so the browser's own
      // Tab then continues from the row rather than from a panel it just lost.
      setOpen(false);
      menu.anchorRef.current?.focus({ preventScroll: true });
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      focusItem(menu.panelRef.current, target, 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusItem(menu.panelRef.current, target, -1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusItem(menu.panelRef.current, null, "first");
    } else if (event.key === "End") {
      event.preventDefault();
      focusItem(menu.panelRef.current, null, "last");
    } else if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      // Whichever side the flyout takes, both arrows open it; Escape steps back.
      if (target.getAttribute("aria-haspopup") !== "menu") return;
      event.preventDefault();
      target.click();
    }
  }

  const subOpen = submenu !== null;
  const chevron = sub.side === "left" ? "‹" : "›";

  return (
    <>
      <button
        type="button"
        ref={menu.anchorRef}
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        className={triggerClass ?? DOTS_TRIGGER}
      >
        {trigger ?? <span aria-hidden>⋯</span>}
      </button>

      {open &&
        createPortal(
          <div
            role="menu"
            aria-label={label}
            ref={menu.panelRef}
            style={menu.style}
            onKeyDown={onMenuKey}
            // `z-modal`, not `z-dropdown`: the portal sits at body level while a
            // Dialog opens a `z-modal` stacking context inside #root. At the same
            // level the later node wins, and a menu opened from inside a dialog
            // has to be on top of it.
            className="jv-elevated z-modal min-w-40 overflow-y-auto p-1"
          >
            {actions.map((action) => (
              <div
                key={action.label}
                role="none"
                className={action.divider ? "mt-1 border-t border-line pt-1" : undefined}
              >
                {action.children ? (
                  <button
                    type="button"
                    role="menuitem"
                    ref={(el) => {
                      if (submenu === action.label) {
                        sub.anchorRef.current = el;
                        subAnchor.current = el;
                      }
                    }}
                    aria-haspopup="menu"
                    aria-expanded={submenu === action.label}
                    onClick={() =>
                      setSubmenu((current) => (current === action.label ? null : action.label))
                    }
                    className={`${itemClass()} flex items-center justify-between gap-2`}
                  >
                    {action.label}
                    <span aria-hidden className="text-muted">
                      {submenu === action.label ? chevron : "›"}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => run(action)}
                    className={itemClass(action.danger)}
                  >
                    {armed === action.label ? action.confirm : action.label}
                  </button>
                )}
              </div>
            ))}
          </div>,
          document.body,
        )}

      {open &&
        subOpen &&
        createPortal(
          <div
            role="menu"
            aria-label={submenu ?? ""}
            ref={sub.panelRef}
            style={sub.style}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                event.preventDefault();
                setSubmenu(null);
                subAnchor.current?.focus({ preventScroll: true });
                return;
              }
              if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
              event.preventDefault();
              focusItem(
                sub.panelRef.current,
                event.target as HTMLElement,
                event.key === "ArrowDown" ? 1 : -1,
              );
            }}
            className="jv-elevated z-modal min-w-44 overflow-y-auto p-1"
          >
            {actions
              .find((action) => action.label === submenu)
              ?.children?.map((child) => (
                <button
                  key={child.label}
                  type="button"
                  role="menuitem"
                  onClick={() => run(child)}
                  className={itemClass(child.danger)}
                >
                  {child.label}
                </button>
              ))}
          </div>,
          document.body,
        )}
    </>
  );
}
